import { Lightning } from "@phosphor-icons/react";

/**
 * Personalized Pre-501(c)(3) Founder Playbook for Melissa Moses & Senior Care Network Inc.
 * Print-optimized: light background, dark text, clean page breaks.
 *
 * Route: /playbook/senior-care-network
 *
 * Greg's flow:
 *   1. Open the URL on a laptop
 *   2. Cmd/Ctrl + P → Save as PDF → email to Melissa
 *   3. Or share the live URL directly (works on mobile)
 *
 * Mirrors PlaybookArkOfRefuge.js — same structure, tailored content.
 */

const Section = ({ eyebrow, title, children, breakBefore = false }) => (
  <section
    className={`mb-12 ${breakBefore ? "print:break-before-page" : ""}`}
    data-testid={`playbook-section-${(eyebrow || title || "").toLowerCase().replace(/\s+/g, "-")}`}
  >
    {eyebrow && (
      <div className="text-emerald-700 font-bold tracking-[0.18em] text-xs uppercase mb-2">
        {eyebrow}
      </div>
    )}
    <h2 className="text-3xl font-black text-zinc-900 mb-6 leading-tight">{title}</h2>
    <div className="text-zinc-800 leading-relaxed space-y-4 text-base">{children}</div>
  </section>
);

const Callout = ({ children, tone = "emerald" }) => {
  const styles =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-500 text-emerald-900"
      : tone === "amber"
      ? "bg-amber-50 border-amber-500 text-amber-900"
      : "bg-zinc-50 border-zinc-400 text-zinc-800";
  return (
    <div className={`border-l-4 ${styles} p-4 rounded-r my-4`}>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const PlaybookSeniorCareNetwork = () => {
  return (
    <div className="min-h-screen bg-white text-zinc-900 print:bg-white" data-testid="playbook-page">
      {/* ─── HEADER BAND ─── */}
      <header className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white px-12 py-10 print:px-10 print:py-8">
        <div className="max-w-[800px] mx-auto">
          <div className="flex items-center gap-2.5 mb-8">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-300" />
            <span className="font-bold tracking-tight text-xl">FlashGrants</span>
          </div>
          <div className="text-emerald-300 font-bold tracking-[0.18em] text-xs uppercase mb-3">
            Pre-501(c)(3) Founder Playbook · Personalized
          </div>
          <h1 className="text-5xl font-black tracking-tight leading-tight mb-3">
            For Melissa &amp; Senior Care Network
          </h1>
          <p className="text-emerald-100 text-lg leading-relaxed max-w-[640px]">
            Your 90-day path from &ldquo;state-filed but funding-blocked&rdquo; to fiscal-sponsored,
            IRS-filed, and applying for the grants Horry County&apos;s seniors deserve to be
            served by &mdash; with the work you&apos;re already doing.
          </p>
          <p className="text-emerald-300/80 text-sm mt-4 font-mono">
            Prepared by Greg James · FlashGrants · June 22, 2026
          </p>
        </div>
      </header>

      {/* ─── BODY ─── */}
      <main className="max-w-[800px] mx-auto px-12 py-12 print:px-10 print:py-8">

        {/* OPENING NOTE */}
        <Section eyebrow="A Word Before You Begin" title="The work was never the problem.">
          <p>
            When your pastor sent you to that church member whose husband had just come
            home from the hospital, you didn&apos;t have a 501(c)(3). You didn&apos;t have a
            board. You didn&apos;t have a grant. You had groceries, a car, and a willingness
            to show up. You taught him how to get back in his vehicle. You wheeled him
            from one room to the next when he couldn&apos;t do it himself. You helped him
            until the day he was hospitalized again — and you grieved him.
          </p>
          <p>
            That&apos;s the work. The work is real. The work is already happening. What you
            need isn&apos;t a different mission. You need a map that gets foundations to fund
            what you&apos;re already doing.
          </p>
          <p>
            This document is that map. Read it once. Print it. Show it to the two board
            candidates you already have in mind. The path is real. The grants exist. And
            you are exactly who they were written for.
          </p>
        </Section>

        {/* SNAPSHOT */}
        <Section eyebrow="Your Foundation — A Quick Snapshot" title="Where Senior Care Network stands today.">
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-5">
            <table className="w-full text-sm">
              <tbody className="space-y-1">
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top w-44">Founder</td><td className="text-zinc-900 py-1.5">Melissa Moses</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Mission</td><td className="text-zinc-900 py-1.5">Wraparound recovery support for seniors 50+ — meal prep + delivery, medical transportation, mobility coaching, grocery runs, and post-hospitalization companionship</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Counties served</td><td className="text-zinc-900 py-1.5">Horry <span className="text-zinc-500">(expanding into Georgetown and Marion when capacity allows)</span></td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Current operating budget</td><td className="text-zinc-900 py-1.5">Under $5,000 cash on hand</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Programs</td><td className="text-zinc-900 py-1.5">Nursing home visits · Hospital-discharge recovery support · Meal delivery · Medical transportation · Mobility coaching · Prayer</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Faith-driven?</td><td className="text-zinc-900 py-1.5">No — secular nonprofit (this changes which grant doors open — see Section 3)</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Year-1 funding goal</td><td className="text-zinc-900 py-1.5">$80,000–$100,000 to serve 60 seniors (~$1,500/senior — defensible unit economics)</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">3-year vision</td><td className="text-zinc-900 py-1.5">Senior recovery network across Horry, Georgetown, and Marion Counties</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Legal status</td><td className="text-zinc-900 py-1.5">SC state nonprofit filing complete · EIN obtained · Business license active · IRS 501(c)(3) not yet filed</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Board</td><td className="text-zinc-900 py-1.5">1 of 3 required directors confirmed · 2 candidates identified</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ─── SECTION 1: THE HYBRID PATH ─── */}
        <Section eyebrow="Section 1 of 5" title="Your path: Fiscal sponsor first, your own 501(c)(3) in parallel." breakBefore>
          <p>
            You&apos;re going to do what most pre-501(c)(3) founders never even know is
            possible — the <strong>Hybrid Path</strong>. You&apos;ll be fiscal-sponsored
            (so foundation grants open up) AND filing your own IRS 501(c)(3) at the
            same time (so long-term you don&apos;t pay a sponsor forever). That&apos;s a
            year of progress compressed into 90 days.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 print:grid-cols-2">
            <div className="border-2 border-emerald-500 rounded-lg p-5 bg-emerald-50/50">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Track 1 · This week</div>
              <h3 className="font-bold text-zinc-900 text-lg mb-2">Coastal Community Foundation Fiscal Sponsorship</h3>
              <p className="text-sm text-zinc-700 leading-snug mb-3">
                CCF&apos;s 9-county service area includes <strong>Horry</strong>, Georgetown,
                Berkeley, Charleston, Dorchester, Colleton, Hampton, Jasper, and Beaufort.
                They host you under their 501(c)(3) umbrella while you operate
                independently and file your own paperwork.
              </p>
              <ul className="text-sm text-zinc-700 space-y-1.5 list-none">
                <li>· Timeline: ~4–6 weeks to onboarded</li>
                <li>· Fee: variable (typically % of grants)</li>
                <li>· Unlocks: Care Fund + the broader SC foundation universe</li>
                <li>· Bonus: CCF brand credibility opens warm doors</li>
              </ul>
            </div>
            <div className="border-2 border-zinc-300 rounded-lg p-5 bg-zinc-50/50">
              <div className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">Track 2 · Parallel filing</div>
              <h3 className="font-bold text-zinc-900 text-lg mb-2">File Your Own IRS 501(c)(3) Application</h3>
              <p className="text-sm text-zinc-700 leading-snug mb-3">
                File directly with the IRS. The streamlined Form 1023-EZ is only available
                to organizations projecting under a specific gross-receipts cap. Because
                your year-1 funding goal is $80K–$100K, the IRS may require the full Form
                1023 instead.
              </p>
              <ul className="text-sm text-zinc-700 space-y-1.5 list-none">
                <li>· Timeline: 1023-EZ ~4–8 weeks · Full 1023 ~3–6 months</li>
                <li>· Cost: current IRS user fee at filing (varies — check pay.gov)</li>
                <li>· Unlocks: federal grants + standalone independence</li>
                <li>· Bonus: no sponsor fees forever after</li>
              </ul>
            </div>
          </div>

          <Callout tone="emerald">
            <strong>Why this is the smartest path:</strong> 4–6 months from now, when your
            IRS determination letter arrives, you &ldquo;graduate&rdquo; out of CCF
            sponsorship and operate standalone. By then you&apos;ll have one or two
            foundation grants on your record and a board of three ready to take over.
            Most pre-501(c)(3) founders pick one path. You&apos;re doing both.
          </Callout>

          <Callout tone="amber">
            <strong>One important honesty point:</strong> Fiscal sponsorship unlocks
            <em> private foundation</em> grants. It does NOT unlock most federal grants
            (Grants.gov, HHS, ACL senior-services programs) — federal funders generally
            require your own IRS 501(c)(3) determination letter and don&apos;t fund through
            sponsors. Federal money is a Track-2 outcome. We&apos;re not chasing it in the
            first 90 days.
          </Callout>

          <h3 className="font-bold text-zinc-900 text-xl mt-8 mb-3">This week&apos;s two specific actions:</h3>
          <ol className="space-y-3 list-decimal list-outside ml-6">
            <li>
              <strong>Confirm your 3rd board member.</strong> You have two candidates in
              mind. Send each of them a short text or email asking if they&apos;ll serve.
              Even a one-line &ldquo;I&apos;m in&rdquo; reply is enough for now — formal
              paperwork can follow within 30 days. Without a 3-person board, the IRS
              won&apos;t approve your 501(c)(3) and many foundations will hesitate.
            </li>
            <li>
              <strong>Visit coastalcommunityfoundation.org</strong> and find the
              &ldquo;Fiscal Sponsorship Program&rdquo; page. Submit the initial inquiry
              form. Expect a response within 7–10 business days requesting more
              information.
            </li>
          </ol>
        </Section>

        {/* ─── SECTION 2: GRANTS TO CHASE ─── */}
        <Section eyebrow="Section 2 of 5" title="The first three grants to chase." breakBefore>
          <p>
            Once you&apos;re fiscal-sponsored through CCF (estimated late July / early
            August 2026), these three grants become available to you. Listed in the
            order you should apply.
          </p>

          <div className="space-y-5 my-6">
            {/* Grant 1 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">First grant · Best fit by far</div>
                  <h3 className="text-lg font-bold text-zinc-900">Sisters of Charity Foundation of SC — Community Engagement Grants</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">$5K–$25K</div>
                  <div className="text-xs text-zinc-500 font-mono">Rolling LOI</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                Sisters of Charity has awarded over $82 million to 3,100+ organizations
                since 1996 — statewide, Catholic-affiliated but not Catholic-restricted.
                Their core focus areas for 2026: economic mobility, housing security,
                <strong> healthcare access</strong>. Senior recovery work fits the
                healthcare-access bucket cleanly. Critically, their eligibility requires
                serving <strong>adult populations</strong> — children-only programs are
                out of scope here. That&apos;s your unfair advantage. Most SC nonprofits
                serve kids. Few serve seniors. The applicant pool is smaller.
              </p>
              <p className="text-sm text-zinc-700 leading-snug mt-2">
                <strong>Recommended ask:</strong> $9,999–$15,000 targeted at your
                hospital-discharge recovery program. Lead the LOI with the story of the
                gentleman your pastor referred — that&apos;s the proof point. Numbers
                second.
              </p>
            </div>

            {/* Grant 2 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Second grant · Currently open · LOI window</div>
                  <h3 className="text-lg font-bold text-zinc-900">Brown Family Foundation — NC/SC Grants</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">$5K–$50K</div>
                  <div className="text-xs text-zinc-500 font-mono">LOI by Aug 10, 2026</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                SC-based foundation funding 501(c)(3) nonprofits in both Carolinas. The
                2026 Letter of Interest window opened June 15. Two-step process: LOI
                first, then if invited, full application by November 9. Awards announced
                December 18.
              </p>
              <p className="text-sm text-zinc-700 leading-snug mt-2">
                <strong>Why this matters:</strong> Almost nobody in the SC nonprofit
                community knows Brown Family exists yet. The applicant pool is small.
                Once you&apos;re sponsored, this is the cleanest first LOI to send.
              </p>
            </div>

            {/* Grant 3 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Third grant · Aligned with your demographic</div>
                  <h3 className="text-lg font-bold text-zinc-900">AARP Community Challenge — 2027 Cycle</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">Varies</div>
                  <div className="text-xs text-zinc-500 font-mono">2027 dates TBD by AARP</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                AARP funds community improvements that benefit residents 50+. That&apos;s
                exactly your demographic. The 2026 cycle has closed; AARP has not yet
                announced 2027 dates as of this playbook&apos;s creation. We&apos;ll
                watch for the announcement and queue you up early. This is a
                later-2026/early-2027 play, not a now play — but it deserves a slot in
                your pipeline because the fit is precise.
              </p>
            </div>
          </div>

          <Callout tone="amber">
            <strong>Two grants explicitly NOT in this lineup:</strong> SC Christian
            Foundation (Senior Care Network is secular, doesn&apos;t qualify), and
            Frances P. Bunnelle Foundation (Georgetown County only — Bunnelle becomes
            relevant once you expand into Georgetown). Both are real doors — just not
            your doors yet.
          </Callout>
        </Section>

        {/* ─── SECTION 3: THE SENIOR-SERVICES NICHE ─── */}
        <Section eyebrow="Section 3 of 5" title="The senior services niche advantage no one tells founders about." breakBefore>
          <p>
            Open up any SC foundation&apos;s grant history and look at who they fund.
            You&apos;ll see the same pattern repeated: children, women, families, youth
            development, homelessness, food insecurity. <strong>Senior-serving
            organizations are noticeably scarce in those lists.</strong>
          </p>
          <p>
            That&apos;s not because seniors don&apos;t need help. It&apos;s because most
            founders who start nonprofits are drawn to children-and-family work.
            It&apos;s emotionally resonant. It photographs well. It moves donors. Seniors,
            statistically, do not get the same emotional pull from the general public —
            even though their needs are just as urgent and, in many cases, more
            expensive to meet.
          </p>
          <p>
            <strong>What this means for you:</strong> You&apos;re in a less-contested
            niche. When a foundation officer reviews 200 applications for a $25,000
            grant, you&apos;re not the 47th application about children with food
            insecurity. You&apos;re the only application about seniors being driven to
            cardiology follow-ups so they don&apos;t die at home. That&apos;s a story
            reviewers remember.
          </p>

          <Callout tone="emerald">
            <p className="font-bold text-emerald-900 mb-2">Paste this paragraph into every grant application you write:</p>
            <p className="text-emerald-950 italic leading-relaxed">
              &ldquo;Senior Care Network Inc. addresses a structurally under-funded need
              in South Carolina: post-hospitalization recovery support for adults 50+ in
              Horry County. Hospital readmission within 30 days of discharge costs
              Medicare an estimated $26 billion annually nationwide — and is most
              preventable through exactly the wraparound work our organization provides:
              transportation to follow-up appointments, grocery support, mobility
              coaching, and a human being who shows up. Our average cost-per-senior of
              ~$1,500 is a fraction of a single readmission.&rdquo;
            </p>
          </Callout>

          <p>
            That paragraph does three things in one move. It frames your work as
            <em> infrastructure</em>, not charity. It cites a real federal cost figure
            reviewers can verify. And it positions you as preventing a more expensive
            outcome — which is the language foundation boards use when they decide what
            to fund.
          </p>
        </Section>

        {/* ─── SECTION 4: 90-DAY PLAN ─── */}
        <Section eyebrow="Section 4 of 5" title="Your 90-day action plan." breakBefore>
          <p>Week by week. One thing at a time. No skipping ahead.</p>

          <div className="space-y-4 my-6">
            {[
              {
                week: "Week 1",
                date: "June 23–29",
                focus: "Foundation in place",
                actions: [
                  "Set up your free account at flash-grants.com",
                  "Take the Mission Statement Diagnostic (5 min — free)",
                  "Send a text or email to both board candidates asking them to confirm they&apos;ll serve",
                  "Open a free Google Drive folder titled &lsquo;Senior Care Network — Foundation Documents&rsquo;",
                ],
              },
              {
                week: "Week 2",
                date: "June 30 – July 6",
                focus: "Paperwork & people",
                actions: [
                  "Submit Coastal Community Foundation Fiscal Sponsorship inquiry at coastalcommunityfoundation.org",
                  "Hold one documented 30-minute board meeting (can be a phone call) with your 3 directors and take minutes",
                  "Take the IRS Form 1023-EZ Eligibility Worksheet to see if you qualify (or if you&apos;ll need the full Form 1023)",
                ],
              },
              {
                week: "Week 3",
                date: "July 7–13",
                focus: "Story collection",
                actions: [
                  "Write the story of three specific seniors Senior Care Network has helped (no names if needed)",
                  "Photograph your program in action: meal prep, transportation, the human moments",
                  "Use the Mission Diagnostic 2–3 more times to refine your statement (Premium Founding Member unlocks unlimited iterations)",
                ],
              },
              {
                week: "Weeks 4–5",
                date: "July 14–27",
                focus: "Sponsor review",
                actions: [
                  "Respond to any CCF follow-up requests for your sponsorship application",
                  "Begin drafting Form 1023 (or 1023-EZ if eligible) in your Google Drive folder",
                  "Schedule your check-in call with Greg",
                ],
              },
              {
                week: "Weeks 6–8",
                date: "Late July – Mid August",
                focus: "Sponsorship approved · Apply",
                actions: [
                  "CCF approves your fiscal sponsorship (expected window)",
                  "Submit Brown Family Foundation LOI before the August 10 deadline",
                  "Submit Sisters of Charity Foundation LOI (rolling — submit as soon as sponsorship is active)",
                ],
              },
              {
                week: "Weeks 9–13",
                date: "Mid August – September",
                focus: "Build the pipeline",
                actions: [
                  "If Brown Family invites a full application, draft and submit by November 9",
                  "Submit IRS Form 1023(-EZ) — pay current user fee at pay.gov",
                  "Track first donations under fiscal sponsor arrangement",
                  "Hold board meeting #2 with documented minutes",
                ],
              },
            ].map((w) => (
              <div key={w.week} className="border-l-4 border-emerald-500 pl-5 py-2">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
                  <h4 className="font-bold text-zinc-900 text-lg">{w.week} <span className="text-zinc-400 font-normal text-sm font-mono">· {w.date}</span></h4>
                  <span className="text-xs uppercase tracking-wider text-emerald-700 font-bold">{w.focus}</span>
                </div>
                <ul className="text-sm text-zinc-700 space-y-1 list-disc list-outside ml-5">
                  {w.actions.map((a, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: a }} />))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* ─── SECTION 5: RESOURCES ─── */}
        <Section eyebrow="Section 5 of 5" title="The resources page." breakBefore>
          <p>Save this. Print it. Pin it on your refrigerator.</p>

          <div className="my-6">
            <h3 className="font-bold text-zinc-900 text-lg mb-3">Fiscal Sponsorship</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>Coastal Community Foundation:</strong> coastalcommunityfoundation.org → &ldquo;Fiscal Sponsorship Program&rdquo; (Horry County is in their 9-county service area)</li>
              <li><strong>Social Good Fund</strong> (backup option, national, secular): socialgoodfund.org</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Your Own 501(c)(3)</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>IRS Form 1023-EZ:</strong> irs.gov → search &ldquo;Form 1023-EZ&rdquo; · Take the Eligibility Worksheet first</li>
              <li><strong>IRS Form 1023 (full):</strong> irs.gov → search &ldquo;Form 1023&rdquo; · Required if you&apos;re projecting over the 1023-EZ revenue cap</li>
              <li><strong>SC Secretary of State Charity Registration:</strong> sos.sc.gov/charity</li>
              <li><strong>Pay.gov (current IRS user fee):</strong> pay.gov</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Grant Research</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>FlashGrants Sunday Drop:</strong> flash-grants.com — five SC grants every Sunday in your inbox</li>
              <li><strong>Sisters of Charity Foundation of SC:</strong> sistersofcharitysc.com/grants/</li>
              <li><strong>Brown Family Foundation:</strong> brownfamilyfoundationsc.org/grant_program/</li>
              <li><strong>AARP Community Challenge:</strong> aarp.org/livable-communities/community-challenge/</li>
              <li><strong>ProPublica Nonprofit Explorer</strong> (free 990 lookups for any foundation): projects.propublica.org/nonprofits</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Bonus: The senior services federal cost figure</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>30-day Medicare readmission cost reference:</strong> Search &ldquo;Medicare hospital readmission cost annual&rdquo; — the $26B/year figure is well-documented in CMS and AHRQ publications and is the strongest framing for senior-recovery grant narratives.</li>
            </ul>
          </div>

          <Callout tone="emerald">
            <strong>Your next call with Greg:</strong> sometime in early-to-mid July
            (we&apos;ll lock the date). Bring: your CCF inquiry confirmation, your board
            confirmation (all 3 directors), and your refined mission statement from the
            Diagnostic. Greg will bring: a draft LOI outline for Brown Family Foundation
            and a check-in on the Sisters of Charity Foundation outreach.
          </Callout>
        </Section>

        {/* CLOSING NOTE */}
        <Section eyebrow="One Last Thing" title="You already know how to do the work. Now you have the map.">
          <p>
            Melissa — you didn&apos;t learn this work in a classroom. You learned it
            because your pastor asked you to show up for one family, and you said yes.
            You taught a man how to get back in his car. You wheeled him from room to
            room. You went to the grocery store for him because he couldn&apos;t go
            himself. You were there until the day he was hospitalized again, and you
            grieved him.
          </p>
          <p>
            That&apos;s not a story you found in a grant application template. It&apos;s
            the proof. Every foundation board that reads it will know two things:
            one, that what you do is real; two, that nobody else applying for that
            grant cycle is doing what you do.
          </p>
          <p className="font-bold text-zinc-900">
            Print this. Keep it close. Take it one week at a time. The 60 seniors
            you&apos;re built to serve are already in Horry County. They&apos;re just
            waiting for the rest of the paperwork to catch up to the work.
          </p>
        </Section>

        {/* FOOTER */}
        <footer className="border-t border-zinc-200 pt-8 mt-12 text-center print:mt-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lightning weight="fill" className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-zinc-900">FlashGrants</span>
          </div>
          <p className="text-xs text-zinc-500 font-mono">
            Built in Myrtle Beach · For South Carolina founders nobody else is building for
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            Personalized for Senior Care Network Inc · Prepared June 22, 2026 · flash-grants.com
          </p>
        </footer>
      </main>

      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default PlaybookSeniorCareNetwork;
